<script setup>
import GoalsPanel from '@/components/goals/GoalsPanel.vue'
import MilestonesPanel from '@/components/milestones/MilestonesPanel.vue'
import NotesPanel from '@/components/notes/NotesPanel.vue'
import PlansPanel from '@/components/plans/PlansPanel.vue'
import ProgressPanel from '@/components/progress/ProgressPanel.vue'
import TimelinePanel from '@/components/timeline/TimelinePanel.vue'
import { relationshipLifecycleChipLabel, relationshipStatusColor } from '@/stores/clients'
import { useClientAccountStore } from '@/stores/clientAccount'
import { useCoachingPlansStore } from '@/stores/coachingPlans'
import { useGoalsStore } from '@/stores/goals'

definePage({
  meta: {
    layout: 'client',
    clientArea: true,
  },
})

const store = useClientAccountStore()
const goalsStore = useGoalsStore()
const plansStore = useCoachingPlansStore()
const { t, te } = useI18n()
const formError = ref('')
const formSuccess = ref('')

const contact = ref({
  name: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  postal_code: '',
  country: '',
})

const healthProposal = ref({
  medical_info: '',
  allergies: '',
  injuries: '',
  health_history: '',
})

const relationshipState = computed(() => store.client?.coaching_relationship?.lifecycle_state ?? null)

const clientLifecycleLabel = computed(() => {
  const state = store.client?.lifecycle_state
  if (!state)
    return '…'

  const key = `clientPortal.lifecycle.${state}`

  return te(key) ? t(key) : t('clientPortal.lifecycle.unknown')
})

onMounted(async () => {
  await store.fetchProfile().catch(() => null)

  const c = store.client
  if (!c)
    return
  contact.value = {
    name: c.name || '',
    phone: c.phone || '',
    address_line1: c.address_line1 || '',
    address_line2: c.address_line2 || '',
    city: c.city || '',
    postal_code: c.postal_code || '',
    country: c.country || '',
  }
  healthProposal.value = {
    medical_info: c.medical_info || '',
    allergies: c.allergies || '',
    injuries: c.injuries || '',
    health_history: c.health_history || '',
  }
  await Promise.all([
    goalsStore.fetchClientOwn().catch(() => null),
    plansStore.fetchClientOwn().catch(() => null),
  ])
})

const saveContact = async () => {
  formError.value = ''
  formSuccess.value = ''
  try {
    await store.updateContact(contact.value)
    formSuccess.value = t('clientPortal.contactSaved')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
}

const submitHealth = async () => {
  formError.value = ''
  formSuccess.value = ''
  try {
    await store.proposeHealthChanges(healthProposal.value)
    formSuccess.value = t('clientPortal.proposalSent')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
}

const router = useRouter()
const deleteDialog = ref(false)
const deleteConfirmation = ref('')
const privacyBusy = ref(false)

const downloadExport = async () => {
  formError.value = ''
  formSuccess.value = ''
  privacyBusy.value = true
  try {
    const payload = await store.exportData()
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')

    anchor.href = url
    anchor.download = `fitx-client-export-${payload.client?.id || 'data'}.json`
    anchor.click()
    URL.revokeObjectURL(url)
    formSuccess.value = t('clientPortal.privacy.exportSuccess')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
  finally {
    privacyBusy.value = false
  }
}

const confirmDeleteData = async () => {
  formError.value = ''
  formSuccess.value = ''
  privacyBusy.value = true
  try {
    await store.deleteData(deleteConfirmation.value)
    deleteDialog.value = false
    await router.replace('/client/login')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
  finally {
    privacyBusy.value = false
  }
}
</script>

<template>
  <div
    class="pa-6 mx-auto"
    style="max-inline-size: 720px;"
  >
    <div class="mb-6">
      <h1 class="text-h4 mb-1">
        {{ $t('clientPortal.title') }}
      </h1>
      <p class="text-medium-emphasis mb-1">
        {{ $t('clientPortal.statusLabel') }} {{ clientLifecycleLabel }}
      </p>
      <VChip
        v-if="relationshipState"
        size="small"
        label
        variant="tonal"
        :color="relationshipStatusColor(relationshipState)"
      >
        {{ $t('clientPortal.relationshipPrefix') }} {{ relationshipLifecycleChipLabel(relationshipState) }}
      </VChip>
    </div>

    <div
      v-if="store.loading && !store.client"
      role="status"
    >
      {{ $t('clientPortal.loading') }}
    </div>

    <template v-else-if="store.client">
      <VAlert
        v-if="formError"
        type="error"
        variant="tonal"
        class="mb-4"
        role="alert"
      >
        {{ formError }}
      </VAlert>
      <VAlert
        v-if="formSuccess"
        type="success"
        variant="tonal"
        class="mb-4"
        role="status"
      >
        {{ formSuccess }}
      </VAlert>

      <VAlert
        v-if="relationshipState === 'paused'"
        type="warning"
        variant="tonal"
        class="mb-4"
        role="status"
      >
        {{ $t('clientPortal.relationshipPaused') }}
      </VAlert>

      <VAlert
        v-if="relationshipState === 'ended'"
        type="info"
        variant="tonal"
        class="mb-4"
        role="status"
      >
        {{ $t('clientPortal.relationshipEnded') }}
      </VAlert>

      <VAlert
        v-if="store.pendingChangeRequest"
        type="info"
        variant="tonal"
        class="mb-4"
        role="status"
      >
        {{ $t('clientPortal.pendingChangeRequestNotice') }}
      </VAlert>

      <TimelinePanel mode="client" />

      <GoalsPanel mode="client" />

      <PlansPanel
        mode="client"
        :can-mutate="relationshipState === 'active'"
      />

      <ProgressPanel
        mode="client"
        :coaching-relationship-id="store.client?.coaching_relationship?.id"
        :can-mutate="relationshipState !== 'ended'"
      />

      <NotesPanel mode="client" />

      <MilestonesPanel mode="client" />

      <VCard
        flat
        border
        class="mb-4"
      >
        <VCardItem>
          <VCardTitle>{{ $t('clientPortal.contactInfoTitle') }}</VCardTitle>
          <VCardSubtitle>{{ $t('clientPortal.contactInfoSubtitle') }}</VCardSubtitle>
        </VCardItem>
        <VCardText>
          <VTextField
            v-model="contact.name"
            :label="$t('common.name')"
            class="mb-3"
          />
          <VTextField
            :model-value="store.client.email"
            :label="$t('common.email')"
            disabled
            class="mb-3"
          />
          <VTextField
            v-model="contact.phone"
            :label="$t('common.phone')"
            class="mb-3"
          />
          <VTextField
            v-model="contact.address_line1"
            :label="$t('common.addressLine1')"
            class="mb-3"
          />
          <VTextField
            v-model="contact.address_line2"
            :label="$t('common.addressLine2')"
            class="mb-3"
          />
          <VRow>
            <VCol cols="4">
              <VTextField
                v-model="contact.city"
                :label="$t('common.city')"
              />
            </VCol>
            <VCol cols="4">
              <VTextField
                v-model="contact.postal_code"
                :label="$t('common.postalCode')"
              />
            </VCol>
            <VCol cols="4">
              <VTextField
                v-model="contact.country"
                :label="$t('common.country')"
              />
            </VCol>
          </VRow>
          <VBtn
            color="primary"
            class="mt-4"
            :loading="store.loading"
            @click="saveContact"
          >
            {{ $t('clientPortal.saveContactInfo') }}
          </VBtn>
        </VCardText>
      </VCard>

      <VCard
        flat
        border
        class="health-section"
      >
        <VCardItem>
          <VCardTitle>{{ $t('clientPortal.healthProfileTitle') }}</VCardTitle>
          <VCardSubtitle>
            {{ $t('clientPortal.healthProfileSubtitle') }}
          </VCardSubtitle>
        </VCardItem>
        <VCardText>
          <p class="text-body-2 mb-2">
            <strong>{{ $t('clientPortal.currentInjuries') }}</strong> {{ store.client.injuries || '—' }}
          </p>
          <p class="text-body-2 mb-2">
            <strong>{{ $t('clientPortal.currentAllergies') }}</strong> {{ store.client.allergies || '—' }}
          </p>
          <p class="text-body-2 mb-4">
            <strong>{{ $t('clientPortal.currentMedicalInfo') }}</strong> {{ store.client.medical_info || '—' }}
          </p>

          <h2 class="text-h6 mb-3">
            {{ $t('clientPortal.proposeChangesTitle') }}
          </h2>
          <VTextarea
            v-model="healthProposal.medical_info"
            :label="$t('clientPortal.proposedMedicalInfoLabel')"
            rows="2"
            class="mb-3"
            :disabled="!!store.pendingChangeRequest"
          />
          <VTextarea
            v-model="healthProposal.allergies"
            :label="$t('clientPortal.proposedAllergiesLabel')"
            rows="2"
            class="mb-3"
            :disabled="!!store.pendingChangeRequest"
          />
          <VTextarea
            v-model="healthProposal.injuries"
            :label="$t('clientPortal.proposedInjuriesLabel')"
            rows="2"
            class="mb-3"
            :disabled="!!store.pendingChangeRequest"
          />
          <VTextarea
            v-model="healthProposal.health_history"
            :label="$t('clientPortal.proposedHealthHistoryLabel')"
            rows="2"
            class="mb-3"
            :disabled="!!store.pendingChangeRequest"
          />
          <VBtn
            color="primary"
            :loading="store.loading"
            :disabled="!!store.pendingChangeRequest"
            @click="submitHealth"
          >
            {{ $t('clientPortal.submitForReview') }}
          </VBtn>
        </VCardText>
      </VCard>

      <VCard
        flat
        border
        class="mb-4"
      >
        <VCardItem>
          <VCardTitle>{{ $t('clientPortal.privacy.title') }}</VCardTitle>
          <VCardSubtitle>{{ $t('clientPortal.privacy.subtitle') }}</VCardSubtitle>
        </VCardItem>
        <VCardText>
          <p class="text-body-2 mb-4">
            {{ $t('clientPortal.privacy.body') }}
          </p>
          <div class="d-flex flex-wrap gap-3">
            <VBtn
              variant="tonal"
              :loading="privacyBusy"
              @click="downloadExport"
            >
              {{ $t('clientPortal.privacy.exportAction') }}
            </VBtn>
            <VBtn
              color="error"
              variant="tonal"
              @click="deleteDialog = true"
            >
              {{ $t('clientPortal.privacy.deleteAction') }}
            </VBtn>
          </div>
        </VCardText>
      </VCard>

      <VDialog
        v-model="deleteDialog"
        max-width="480"
      >
        <VCard>
          <VCardTitle>{{ $t('clientPortal.privacy.deleteTitle') }}</VCardTitle>
          <VCardText>
            <p class="mb-4">
              {{ $t('clientPortal.privacy.deleteBody') }}
            </p>
            <VTextField
              v-model="deleteConfirmation"
              :label="$t('clientPortal.privacy.deleteConfirmLabel')"
              autocomplete="off"
            />
          </VCardText>
          <VCardActions>
            <VSpacer />
            <VBtn
              variant="text"
              @click="deleteDialog = false"
            >
              {{ $t('common.cancel') }}
            </VBtn>
            <VBtn
              color="error"
              :loading="privacyBusy"
              :disabled="deleteConfirmation !== 'DELETE MY DATA'"
              @click="confirmDeleteData"
            >
              {{ $t('clientPortal.privacy.deleteConfirmAction') }}
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>
    </template>
  </div>
</template>

<style scoped>
.health-section {
  border-inline-start: 4px solid rgb(var(--v-theme-warning));
}
</style>
